/**
 * Persistent media-contract-v2 player — one media element for the whole session.
 *
 * Live playlists publish honest EXT-X-GAP entries instead of waiting for every
 * rendition to be ready, so audio can play before video heals in; that alone
 * only flips the badge (the pure `mediaStateAt` over the coverage unions),
 * never the element. A finalized snapshot heals those gaps by rendering a
 * fresh `?fin=N` revision — the master's children change under it without the
 * master's own URL changing, so `snapshotRev` rides in as a prop: a change
 * reloads the source on this same element and resumes at the same UTC. That
 * is the one case the "never remount" rule yields to.
 *
 * Deliberately tiny: no camera fan-out (v2 sessions are single-phone), no tier
 * picker (renditions are canonical), no PDT re-anchoring (the playlists carry
 * PROGRAM-DATE-TIME from phone-attested UTC bounds).
 */

import Hls from 'hls.js';
import { VolumeX } from 'lucide-react';
import { forwardRef, ReactNode, useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react';

import { Spinner } from '@/components/ui/spinner';
import { cn } from '@/lib/utils';
import PlayerChrome, { GlassRailData, PlayerTransport } from '../PlayerChrome';
import { entrySeekClampIntent, EntryIntent } from './entryIntent';
import { intersectRanges, utcToMediaSeconds } from './firstOverlap';
import {
  MAX_MEDIA_RECOVERIES,
  MEDIA_RECOVERY_RESET_MS,
  monotonicMountRev,
  MountedRevision,
  resolveResilientFlag,
  ResilientMode,
} from './resilientPlayback';
import { useResilientPlayback } from './useResilientPlayback';
import { toVideoRotation, useVideoRotationStyle } from '@/lib/videoRotation';
import {
  clampSeekSeconds,
  DEFAULT_SEGMENT_DURATION_S,
  liveEdgeUtcMsFromRegion,
  LIVE_EDGE_SYNC_SEGMENTS,
  pickLiveEdgeRegion,
} from './seekClamp';
import {
  AvailabilityBands,
  buildAvailabilityBands,
  mediaStateAt,
  MediaStateKind,
  TimeRange,
} from './availability';

/**
 * Imperative handle for hosts that drive the playhead from wall-clock UTC
 * (the synced viewer seeks here when the operator clicks the map or a chart).
 */
export interface V2PlayerHandle {
  /** Seek to the moment whose absolute UTC matches `utcMs`. */
  seekToUtc: (utcMs: number) => void;
  play: () => void;
  pause: () => void;
}

export interface V2PlayerProps {
  /** Absolute or root-relative v2 master URL (operator route or /share/{token}/v2/...). */
  masterUrl: string;
  /** Independent readiness unions from the coverage endpoint (UTC ms pairs). */
  audioUnion?: TimeRange[];
  videoUnion?: TimeRange[];
  /**
   * Coverage's `playable_union` (UTC ms pairs) — real, fetchable media,
   * unlike `seekable`, which also spans EXT-X-GAP cells. Feeds the
   * gap-aware live-edge clamp (see `seekClamp.ts`) and, with
   * `startAtLiveEdge`, the initial live-edge position.
   */
  playableUnion?: TimeRange[];
  /**
   * Coverage's `snapshot_rev` — null until a finalized snapshot exists. A
   * change reloads this element's source in place (same UTC, same
   * paused/playing state, same volume) instead of leaving the healed media
   * unreachable behind a master URL that never changes.
   */
  snapshotRev?: number | null;
  /** Coverage's `coverage_life` — a change means the session was purged and recreated. */
  coverageLife?: string | null;
  autoPlay?: boolean;
  muted?: boolean;
  /**
   * Whether this recording has an audio track at all. False shows "No audio" in the controls and
   * drops the tap-for-sound prompt; null/undefined (older phone builds) shows nothing.
   */
  audioExpected?: boolean | null;
  /** Coverage's `rotation`: clockwise degrees that turn the recording upright. Anything but 90/180/270 plays as recorded. */
  rotation?: number | null;
  className?: string;
  /** Playhead as UTC ms (from the playlist PROGRAM-DATE-TIME), null until known. */
  onUtcUpdate?: (utcMs: number | null) => void;
  /** Fires once the timeline's first PROGRAM-DATE-TIME is known; UTC ms. */
  onPdtReady?: (firstPdtMs: number) => void;
  /**
   * One-shot seek applied as soon as the timeline anchor is known — a deep
   * link, a saved resume position, or a lap/stage entry point. Re-applied if
   * the value changes, so a second entry point seeks again. `live` intent
   * must pass null here and let the player place itself at the live edge.
   */
  initialSeekUtcMs?: number | null;
  /**
   * Why this mount is opening where it is — required so a host can never
   * silently default into the wrong start position (see `entryIntent.ts`).
   * Governs the entry-point seek's clamp behavior and, with `live` and
   * `startAtLiveEdge`, whether the player places itself at the live edge.
   */
  entryIntent: EntryIntent;
  /**
   * Live session — a UTC seek outside the rolling window resumes near the live
   * edge instead of at the oldest buffered segment (see `clampSeekSeconds`).
   */
  live?: boolean;
  /**
   * Open a live session at the newest playable position instead of media
   * time 0. Off by default: the operator's synced viewer also sets `live`
   * but wants from-the-start playback, so this is a separate opt-in rather
   * than riding on `live` itself.
   */
  startAtLiveEdge?: boolean;
  /** Fires only when the badge truth changes — never accompanied by a remount. */
  onMediaState?: (state: MediaStateKind) => void;
  /**
   * Fires once playback is judged unrecoverable for this `masterUrl` — a
   * fatal MEDIA/OTHER error, or a fatal NETWORK_ERROR (e.g. the master
   * 404s, which happens in normal operation whenever a v2 session's
   * playlist isn't reachable yet) that has exceeded its bounded retry
   * budget. Lets the parent stop treating this session as v2-playable
   * and show its own fallback/error UI instead of a spinner that would
   * otherwise retry `hls.startLoad()` forever.
   */
  onFatalError?: (detail: string) => void;
  /**
   * Set to mount the app's own chrome (playbar, glass rail, Maximise) via
   * `PlayerChrome`, so a v2 session is indistinguishable from a legacy one.
   * `full` reserves the controls (dashboard), `minimal` goes immersive and auto-hides
   * them (fan viewer). Omitted, the element keeps native browser controls —
   * right for bare embeds with no surrounding chrome to match.
   */
  chrome?: 'full' | 'minimal';
  /** Height-bound the picture without entering fullscreen (mobile landscape). */
  heightFit?: boolean;
  glassRail?: GlassRailData | null;
  /** Fullscreen a parent shell instead of the player; `maximized` drives the icon. */
  onMaximize?: () => void;
  maximized?: boolean;
  onControlsHiddenChange?: (hidden: boolean) => void;
  /**
   * Replaces PlayerChrome's playbar with the host's own controls, drawn over
   * the picture. Needs `chrome` set too — without it the element keeps its
   * native controls and the transport is never populated.
   */
  renderControls?: (transport: PlayerTransport, state: PlayerOverlayState) => ReactNode;
}

/** What a `renderControls` host needs beyond the transport. */
export interface PlayerOverlayState {
  /** Autoplay started muted and the viewer hasn't unmuted yet. */
  tapForSound: boolean;
}

/** Bounded retry budget for a fatal NETWORK_ERROR (e.g. a 404 master) —
 *  without a bound, hls.js's own recommended `startLoad()` retry on every
 *  fatal network error becomes an unbounded tight loop against a URL that
 *  will never start responding (default-off share-token flag, a session
 *  that never advertised v2, etc). */
const MAX_NETWORK_RETRIES = 3;

/** How long a seek can sit unresolved before the pending indicator gives way
 *  to a retryable message — long enough to cover normal segment fetch/decode
 *  stalls, short enough that a genuinely stuck seek doesn't look frozen. */
const SEEK_PENDING_TIMEOUT_MS = 10_000;

/** hls.js's own `backBufferLength` default is Infinity — a tab retains every
 *  played segment for the life of the session. Bounded here so long-running
 *  playback (a race that runs for hours) doesn't grow memory unbounded. */
const BACK_BUFFER_LENGTH_S = 30;

/** Converts the resilient controller's four-way mode into the legacy two-medium badge kind, for hosts still reading `onMediaState`. */
function resilientModeToLegacyState(mode: ResilientMode): MediaStateKind {
  if (mode === 'audioOnly') return 'audio';
  if (mode === 'both' || mode === 'videoOnly') return 'video';
  return 'gap';
}

// `syncUnmute` must write the element's `.muted` synchronously in the
// gesture handler — WebKit won't credit a mute change made later from a
// `useEffect` as part of the user gesture. `dismissed` latches once that
// fires, so a later deliberate re-mute doesn't resurrect the overlay.
function useTapForSound(
  chromeMuted: boolean,
  setChromeMuted: (muted: boolean) => void,
  syncUnmute: () => void,
  autoPlay: boolean,
  muted: boolean,
) {
  const eligibleRef = useRef(autoPlay && muted);
  const [dismissed, setDismissed] = useState(false);
  useEffect(() => {
    if (!chromeMuted) setDismissed(true);
  }, [chromeMuted]);
  return {
    visible: eligibleRef.current && chromeMuted && !dismissed,
    // The mute/unmute button already toggles off `transport.muted` itself
    // on click — it must run untouched here, or firing on its pointerdown
    // too races the button's own click, which reads `transport.muted`
    // *after* this has already flipped it, and toggles it straight back.
    onGesture: (event: { target: EventTarget | null }) => {
      if (!chromeMuted) return;
      if ((event.target as Element | null)?.closest('[aria-label="Mute"], [aria-label="Unmute"]')) return;
      syncUnmute();
      setChromeMuted(false);
    },
  };
}

function TapForSoundOverlay() {
  return (
    <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
      <div className="flex items-center gap-1.5 rounded bg-black/70 px-2 py-1 text-[10px] font-bold text-white">
        <VolumeX className="h-3 w-3" />
        tap for sound
      </div>
    </div>
  );
}

/**
 * Independent-audio path: one `<audio>` element owns sound and the clock
 * through its own Hls instance on the audio-only child; a muted `<video>`
 * follows along on the video-only child and hides its picture while stale
 * or unavailable. See `useResilientPlayback.ts` for the controller and
 * `resilientPlayback.ts` for the pure mode/drift decisions it applies.
 */
const ResilientV2Player = forwardRef<V2PlayerHandle, V2PlayerProps>(function ResilientV2Player(
  {
    masterUrl,
    playableUnion,
    snapshotRev = null,
    coverageLife = null,
    autoPlay = false,
    muted = false,
    live = false,
    startAtLiveEdge = false,
    initialSeekUtcMs,
    entryIntent,
    className,
    onUtcUpdate,
    onPdtReady,
    onMediaState,
    onFatalError,
    chrome,
    heightFit = false,
    glassRail = null,
    onMaximize,
    maximized = false,
    audioExpected = null,
    rotation = null,
    onControlsHiddenChange,
    renderControls,
  },
  ref,
) {
  const { audioRef, videoRef, mode, videoReady, fatal, transport, handle, goLive } = useResilientPlayback({
    masterUrl,
    snapshotRev,
    coverageLife,
    live,
    startAtLiveEdge,
    autoPlay,
    muted,
    playableUnion,
    initialSeekUtcMs,
    entryIntent,
    onUtcUpdate,
    onPdtReady,
    onMediaState: onMediaState && ((next) => onMediaState(resilientModeToLegacyState(next))),
    onFatalError,
  });
  const rotationStyle = useVideoRotationStyle(videoRef, toVideoRotation(rotation));

  useImperativeHandle(ref, () => handle, [handle]);

  const tapForSound = useTapForSound(
    transport.muted,
    transport.setMuted,
    () => {
      if (audioRef.current) audioRef.current.muted = false;
    },
    autoPlay,
    muted,
  );

  const overlays = (
    <>
      {mode === 'audioOnly' && (
        <div className="pointer-events-none absolute left-2 top-2 rounded bg-cyan-500/90 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-black">
          Live sound
        </div>
      )}
      {mode === 'videoOnly' && (
        <div className="pointer-events-none absolute left-2 top-2 rounded bg-amber-500/90 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-black">
          Silent video
        </div>
      )}
      {mode === 'waiting' && !fatal && (
        <div className="pointer-events-none absolute inset-0 flex items-center justify-center bg-black/50">
          <Spinner size={24} />
        </div>
      )}
      {live && startAtLiveEdge && (
        <button
          type="button"
          onClick={goLive}
          className="absolute right-2 top-2 rounded bg-black/70 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-white hover:bg-black/90"
        >
          Go live
        </button>
      )}
      {fatal && (
        <div className="absolute inset-0 flex items-center justify-center bg-black/70 p-4 text-center text-xs text-white">
          {fatal}
        </div>
      )}
    </>
  );

  // Hidden when chrome-driven (its own transport owns play/seek/volume);
  // given native controls as the bare-embed fallback since it is the sound
  // and clock owner, unlike the muted, follower <video>.
  const audioEl = (
    <audio
      ref={audioRef}
      muted={muted}
      controls={!chrome}
      className={cn(!chrome ? 'absolute inset-x-0 bottom-0 w-full' : 'hidden')}
    />
  );
  const videoEl = (
    <video
      ref={videoRef}
      className={cn(
        'h-full w-full object-contain transition-opacity duration-300',
        videoReady ? 'opacity-100' : 'opacity-0',
      )}
      style={rotationStyle}
      playsInline
    />
  );

  if (!chrome) {
    return (
      <div className={cn('relative h-full w-full bg-black', className)}>
        {videoEl}
        {audioEl}
        {overlays}
      </div>
    );
  }

  if (renderControls) {
    return (
      <div
        className={cn('relative h-full w-full bg-black', className)}
        onClickCapture={tapForSound.onGesture}
        onKeyDownCapture={tapForSound.onGesture}
        onPointerDownCapture={tapForSound.onGesture}
      >
        {videoEl}
        {audioEl}
        {overlays}
        {renderControls(transport, {
          tapForSound: tapForSound.visible && !fatal && audioExpected !== false,
        })}
      </div>
    );
  }

  return (
    // display:contents: stays out of PlayerChrome's flex layout while still
    // letting these capture-phase listeners see every gesture, including
    // the playbar's — a DOM sibling of the picture, not a descendant of it.
    // onPointerDownCapture also covers touch/pen, where Radix's Slider
    // preventDefaults its own pointerdown and suppresses the synthetic
    // click that onClickCapture alone would otherwise depend on.
    <div
      className="contents"
      onClickCapture={tapForSound.onGesture}
      onKeyDownCapture={tapForSound.onGesture}
      onPointerDownCapture={tapForSound.onGesture}
    >
      <PlayerChrome
        transport={transport}
        variant={chrome}
        heightFit={heightFit}
        glassRail={glassRail}
        onMaximize={onMaximize}
        isMaximized={maximized}
        audioExpected={audioExpected}
        onControlsHiddenChange={onControlsHiddenChange}
      >
        {({ fitTiles }) => (
          <div className={cn('relative flex-grow bg-black', fitTiles && 'min-h-0', className)}>
            {videoEl}
            {audioEl}
            {overlays}
            {tapForSound.visible && !fatal && audioExpected !== false && <TapForSoundOverlay />}
          </div>
        )}
      </PlayerChrome>
    </div>
  );
});

const LegacyV2Player = forwardRef<V2PlayerHandle, V2PlayerProps>(function LegacyV2Player(
  {
    masterUrl,
    audioUnion,
    videoUnion,
    playableUnion,
    snapshotRev = null,
    coverageLife = null,
    autoPlay = false,
    muted = false,
    live = false,
    startAtLiveEdge = false,
    className,
    onUtcUpdate,
    onPdtReady,
    initialSeekUtcMs,
    entryIntent,
    onMediaState,
    onFatalError,
    chrome,
    heightFit = false,
    glassRail = null,
    onMaximize,
    maximized = false,
    audioExpected = null,
    rotation = null,
    onControlsHiddenChange,
    renderControls,
  },
  ref,
) {
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const rotationStyle = useVideoRotationStyle(videoRef, toVideoRotation(rotation));
  const hlsRef = useRef<Hls | null>(null);
  // What the mount effect last built its instance from — tells a brand new
  // session (different masterUrl: hard reset) apart from a healed finalized
  // snapshot on the SAME session (same masterUrl, new snapshotRev: seamless
  // reload).
  const previousMountRef = useRef<MountedRevision | null>(null);
  const mediaRecoveriesRef = useRef(0);
  const lastMediaRecoveryAtRef = useRef(0);
  const autoPlayRef = useRef(autoPlay);
  autoPlayRef.current = autoPlay;
  // Set on a fresh 'live' mount with no initialSeekUtcMs; cleared the moment
  // the live-edge placement is actually applied, or a real user seek
  // preempts it. Never set on a revision reload — that path resumes the
  // captured UTC instead (see `resume` below).
  const livePlacementPendingRef = useRef(false);
  const [mediaState, setMediaState] = useState<MediaStateKind>('gap');
  const [fatal, setFatal] = useState<string | null>(null);
  // Feedback for the gap between a seek and playback resuming — otherwise a
  // seek that takes a moment (or one hls.js can't resolve at all) looks like
  // the player just froze.
  const [seekPending, setSeekPending] = useState(false);
  const [seekTimedOut, setSeekTimedOut] = useState(false);

  const bands: AvailabilityBands = useMemo(
    () => buildAvailabilityBands({ audio: audioUnion ?? [], video: videoUnion ?? [] }),
    [audioUnion, videoUnion],
  );
  const bandsRef = useRef(bands);
  bandsRef.current = bands;
  // Raw unions (not the highest-availability bands) for the first-overlap
  // start seek below — read live inside the mount effect's closures without
  // making audioUnion/videoUnion retrigger that effect.
  const unionsRef = useRef({ audio: audioUnion ?? [], video: videoUnion ?? [], playable: playableUnion ?? [] });
  unionsRef.current = { audio: audioUnion ?? [], video: videoUnion ?? [], playable: playableUnion ?? [] };
  // EXT-X-TARGETDURATION off the live playlist (hls.js parses it for us) —
  // the real segment length when known, for the live-edge backoff below.
  const targetDurationRef = useRef<number | null>(null);
  const callbacksRef = useRef({ onUtcUpdate, onPdtReady, onMediaState, onFatalError });
  callbacksRef.current = { onUtcUpdate, onPdtReady, onMediaState, onFatalError };
  const stateRef = useRef<MediaStateKind>('gap');
  const liveRef = useRef(live);
  liveRef.current = live;
  // UTC at media time 0, known from the playlist alone — a host can seek before
  // the first frame ever plays. `positionRef` is the last reported playhead UTC.
  // `anchorReady` mirrors the ref into render state purely so the initial-seek
  // effect below has something to depend on.
  const anchorRef = useRef<number | null>(null);
  const positionRef = useRef<number | null>(null);
  const [anchorReady, setAnchorReady] = useState(false);
  // The initialSeekUtcMs value already applied. anchorReady flips again on a
  // seamless reload (the new source needs its own fresh anchor); without this,
  // that second flip would replay a deep link the viewer has since moved past.
  const appliedSeekRef = useRef<number | null | undefined>(undefined);
  // One-shot per mount: starts playback at the first audio/video overlap, or
  // (startAtLiveEdge) the newest playable position, instead of media time 0.
  const autoStartSeekAppliedRef = useRef(false);

  // playableUnion converted to media seconds via the current anchor, for the
  // gap-aware live-edge clamp. Empty (falls back to `seekable`) until the
  // anchor is known.
  const playableRangesSeconds = useCallback((): Array<[number, number]> => {
    const anchor = anchorRef.current;
    if (anchor === null) return [];
    return unionsRef.current.playable.map(
      ([start, end]) => [utcToMediaSeconds(start, anchor), utcToMediaSeconds(end, anchor)] as [number, number],
    );
  }, []);

  // The live-edge target "Go live" and the automatic live placement both
  // apply: the latest contiguous PUBLISHED audio region (video only when
  // audio has none — see `unionsRef`'s coverage-sourced unions), backed off
  // ~2 segment durations. Null until the anchor is known (nothing to convert
  // UTC to media seconds with) or neither medium has published anything yet.
  const liveEdgeTargetSeconds = useCallback((): { seconds: number; rangeSeconds: [number, number] } | null => {
    const anchor = anchorRef.current;
    if (anchor === null) return null;
    const audio = unionsRef.current.audio;
    const video = unionsRef.current.video;
    const region = pickLiveEdgeRegion(
      audio.length ? audio[audio.length - 1] : null,
      video.length ? video[video.length - 1] : null,
    );
    if (!region) return null;
    const targetUtcMs = liveEdgeUtcMsFromRegion(region, targetDurationRef.current ?? DEFAULT_SEGMENT_DURATION_S);
    return {
      seconds: utcToMediaSeconds(targetUtcMs, anchor),
      rangeSeconds: [utcToMediaSeconds(region[0], anchor), utcToMediaSeconds(region[1], anchor)],
    };
  }, []);

  // Transport state, mirrored off the element so the shared chrome can render
  // it. Only populated when this player owns the chrome — with native controls
  // the browser reads the element directly.
  const [isPlaying, setIsPlaying] = useState(false);
  // Mirrors `isPlaying` synchronously so a reload can read "was it playing"
  // without waiting on React's own state batching.
  const isPlayingRef = useRef(false);
  const [position, setPosition] = useState(0);
  const [duration, setDuration] = useState(0);
  const [canPlay, setCanPlay] = useState(false);
  const [chromeMuted, setChromeMuted] = useState(muted);
  const [volume, setVolume] = useState(0.8);
  const tapForSound = useTapForSound(
    chromeMuted,
    setChromeMuted,
    () => {
      if (videoRef.current) videoRef.current.muted = false;
    },
    autoPlay,
    muted,
  );
  // Fresh retry budget per hls.js instance — the effect below creates a new
  // one whenever masterUrl or snapshotRev changes.
  const networkRetriesRef = useRef(0);

  useImperativeHandle(
    ref,
    () => ({
      seekToUtc: (utcMs: number) => {
        const video = videoRef.current;
        if (!video || !Number.isFinite(utcMs)) return;
        const playhead = positionRef.current;
        let target: number;
        if (playhead !== null) {
          // Offset from the reported playhead rather than from the anchor: it stays
          // exact across EXT-X-DISCONTINUITY, where media time and wall time are no
          // longer one linear map.
          target = video.currentTime + (utcMs - playhead) / 1000;
        } else if (anchorRef.current !== null) {
          target = (utcMs - anchorRef.current) / 1000;
        } else {
          // No anchor yet — this call cannot do anything. Leave a pending
          // automatic live placement alone: a no-op caller seek must never
          // cancel it, or a "Go live"/seek pressed before any data has
          // loaded would strand the player with neither seek ever applied.
          return;
        }
        // A real caller-driven seek always preempts a still-pending automatic
        // live-edge placement — landing there after the viewer has already
        // moved elsewhere would silently discard their seek.
        livePlacementPendingRef.current = false;
        video.currentTime = clampSeekSeconds(video, target, {
          live: liveRef.current,
          intent: 'explicit',
          playableRanges: playableRangesSeconds(),
        });
      },
      play: () => {
        void videoRef.current?.play().catch(() => undefined);
      },
      pause: () => videoRef.current?.pause(),
    }),
    [playableRangesSeconds],
  );

  // Mounts the source once per masterUrl; a snapshotRev change (a finalized
  // snapshot healed) reloads it in place on this same element instead —
  // the one case the persistent-element contract yields to. Everything else
  // that changes DURING the session (badge, unions) reuses this instance.
  // `mountedSnapshotRev` floors out-of-order polls at the rev already
  // mounted (see `monotonicMountRev`), so a lower value can't fire a reload.
  const mountedSnapshotRev = monotonicMountRev(previousMountRef.current, masterUrl, coverageLife, snapshotRev);

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return undefined;

    const previousMount = previousMountRef.current;
    // A new life is a different recording, so it resets rather than reloads in place.
    const isRevisionReload =
      previousMount !== null &&
      previousMount.masterUrl === masterUrl &&
      previousMount.life === coverageLife &&
      previousMount.rev !== mountedSnapshotRev;
    previousMountRef.current = { masterUrl, life: coverageLife, rev: mountedSnapshotRev };

    // Capture exactly what a mid-watch viewer must not lose, before anything
    // below tears down the instance that is currently playing.
    const resume = isRevisionReload
      ? {
          utcMs: positionRef.current,
          paused: !isPlayingRef.current,
          muted: video.muted,
          volume: video.volume,
        }
      : null;

    setFatal(null);
    setSeekPending(false);
    setSeekTimedOut(false);
    networkRetriesRef.current = 0;
    // The healed/finalized playlist can start earlier than the live one it
    // replaces (it may carry more history), so media-time-0's UTC must be
    // recomputed even on a seamless reload — only the absolute UTC captured
    // above survives the swap, never the old anchor.
    anchorRef.current = null;
    positionRef.current = null;
    setAnchorReady(false);
    targetDurationRef.current = null;
    if (!isRevisionReload) {
      appliedSeekRef.current = undefined;
    }
    autoStartSeekAppliedRef.current = false;
    // A revision reload resumes the captured UTC instead (see `resume`
    // above) — only a fresh 'live' mount with nothing else to apply defers
    // to the live-edge placement below.
    livePlacementPendingRef.current =
      !isRevisionReload && entryIntent === 'live' && startAtLiveEdge && initialSeekUtcMs == null;

    // An old instance's queued hls.js event or pending retry must never act
    // on state a newer instance already owns.
    let destroyed = false;

    // Applies the same live-edge target the "Go live" button uses (see
    // `liveEdgeTargetSeconds`), once the anchor and a published region both
    // exist — never guesses off `video.duration`/`seekable` alone, so it can
    // never land on an unpublished last fragment. Also waits for a real
    // media bound (seekable, buffered, or a known duration): hls.js's own
    // `startPosition: -1` config (below) makes it seek to ITS OWN computed
    // start position the first time any buffered range appears
    // (seekToStartPos), which can silently clobber a `currentTime` write
    // issued earlier — waiting for that same trigger condition here means
    // our write always lands after, never before, hls.js's own attempt.
    // Runs on every bounds/anchor-relevant event but only ever acts once,
    // since `livePlacementPendingRef` is cleared the instant it fires (or
    // the viewer seeks first — see the imperative handle and transport.seek).
    const attemptLivePlacement = () => {
      if (destroyed || !livePlacementPendingRef.current) return;
      if (!Number.isFinite(video.duration) && video.seekable.length === 0 && video.buffered.length === 0) return;
      const target = liveEdgeTargetSeconds();
      if (!target) return;
      livePlacementPendingRef.current = false;
      video.currentTime = clampSeekSeconds(video, target.seconds, {
        live: true,
        intent: 'explicit',
        playableRanges: [target.rangeSeconds],
      });
    };

    const noteAnchor = (utcMs: number) => {
      if (destroyed || anchorRef.current !== null || !Number.isFinite(utcMs)) return;
      anchorRef.current = utcMs;
      setAnchorReady(true);
      callbacksRef.current.onPdtReady?.(utcMs);
      if (resume) {
        if (resume.utcMs !== null) {
          video.currentTime = clampSeekSeconds(video, (resume.utcMs - utcMs) / 1000, {
            live: liveRef.current,
            intent: 'resume',
            playableRanges: playableRangesSeconds(),
          });
        }
        video.muted = resume.muted;
        video.volume = resume.volume;
        if (!resume.paused) void video.play().catch(() => undefined);
      } else if (
        initialSeekUtcMs == null &&
        !autoStartSeekAppliedRef.current &&
        !livePlacementPendingRef.current
      ) {
        // Head-of-timeline audio-only gap: the browser only plays where audio
        // AND video buffers overlap, so starting at 0 spins forever with no
        // error. Jump to the first point both are actually available. Gated
        // on the PENDING flag, not the intent itself: a 'live' mount whose
        // placement can never actually happen (e.g. the host's declared
        // intent is stale and startAtLiveEdge has already been withdrawn)
        // must still get this fallback instead of sitting stuck at time 0.
        const overlap = intersectRanges(unionsRef.current.audio, unionsRef.current.video)[0];
        if (overlap) {
          const seconds = utcToMediaSeconds(overlap[0], utcMs);
          if (seconds > 0) {
            autoStartSeekAppliedRef.current = true;
            video.currentTime = seconds;
            console.debug(`v2 player: starting at first audio/video overlap (+${seconds.toFixed(1)}s)`);
          }
        }
      }
      attemptLivePlacement();
    };
    // Native HLS (Safari) publishes the timeline's start date on the element; in
    // Chrome `getStartDate` is absent and the hls.js handler below supplies it.
    const noteNativeAnchor = () => {
      const native = video as HTMLVideoElement & { getStartDate?: () => Date };
      const start = native.getStartDate?.();
      if (start instanceof Date && Number.isFinite(start.getTime())) {
        noteAnchor(start.getTime());
      }
    };
    video.addEventListener('loadedmetadata', noteNativeAnchor);

    // Element state the shared chrome renders. `durationchange` matters as much
    // as `loadedmetadata` here: a live playlist's duration grows as cells are
    // appended, and a seek bar pinned to the first-known length would refuse to
    // reach the newest media.
    const syncTransport = () => {
      setPosition(video.currentTime);
      setDuration(Number.isFinite(video.duration) ? video.duration : 0);
      attemptLivePlacement();
    };
    const onPlay = () => {
      isPlayingRef.current = true;
      setIsPlaying(true);
    };
    const onPause = () => {
      isPlayingRef.current = false;
      setIsPlaying(false);
    };
    const onCanPlay = () => setCanPlay(true);
    video.addEventListener('timeupdate', syncTransport);
    video.addEventListener('durationchange', syncTransport);
    video.addEventListener('loadedmetadata', syncTransport);
    video.addEventListener('seeked', syncTransport);
    video.addEventListener('play', onPlay);
    video.addEventListener('pause', onPause);
    video.addEventListener('canplay', onCanPlay);

    // Feedback for the gap between a seek and playback resuming. `seeking`
    // opens the window, `seeked`/`canplay` close it; a stuck seek (network
    // stall, an hls.js retry loop) falls back to a retryable message instead
    // of leaving the spinner up forever.
    let seekTimeoutId: number | null = null;
    const clearSeekTimeout = () => {
      if (seekTimeoutId !== null) {
        window.clearTimeout(seekTimeoutId);
        seekTimeoutId = null;
      }
    };
    const onSeekResolved = () => {
      clearSeekTimeout();
      setSeekPending(false);
      setSeekTimedOut(false);
    };
    const onSeeking = () => {
      setSeekTimedOut(false);
      setSeekPending(true);
      clearSeekTimeout();
      seekTimeoutId = window.setTimeout(() => {
        seekTimeoutId = null;
        setSeekPending(false);
        setSeekTimedOut(true);
      }, SEEK_PENDING_TIMEOUT_MS);
    };
    video.addEventListener('seeking', onSeeking);
    video.addEventListener('seeked', onSeekResolved);
    video.addEventListener('canplay', onSeekResolved);

    const reportPosition = () => {
      let utcMs: number | null = null;
      const hls = hlsRef.current;
      const playing = hls?.playingDate;
      if (playing instanceof Date && Number.isFinite(playing.getTime())) {
        utcMs = playing.getTime();
      } else {
        // Native HLS (Safari): EXT-X-PROGRAM-DATE-TIME surfaces via getStartDate(), which
        // lib.dom does not declare on every TS target — feature-detect it.
        const native = video as HTMLVideoElement & { getStartDate?: () => Date };
        const start = native.getStartDate?.();
        if (start instanceof Date && Number.isFinite(start.getTime())) {
          utcMs = start.getTime() + video.currentTime * 1000;
        }
      }
      positionRef.current = utcMs;
      callbacksRef.current.onUtcUpdate?.(utcMs);
      if (utcMs !== null) {
        noteAnchor(utcMs - video.currentTime * 1000);
        const next = mediaStateAt(utcMs, bandsRef.current);
        if (next !== stateRef.current) {
          stateRef.current = next;
          setMediaState(next);
          callbacksRef.current.onMediaState?.(next);
        }
      }
    };
    video.addEventListener('timeupdate', reportPosition);

    if (Hls.isSupported()) {
      const hls = new Hls({
        // Cookie-session auth — the v2 master/audio/video playlist,
        // media-segment, and init-segment routes are gated by the session's event
        // (operator bearer, owning device, or the anonymous share/playback gate —
        // see app/api/v2_playback.py's route guards). hls.js issues its own XHRs
        // outside axios/fetch's credential config, so it needs an explicit
        // opt-in: withCredentials=true makes the browser
        // attach the (HttpOnly) session cookie to these same-origin XHRs too. No
        // CSRF header needed — every request hls.js makes here is a GET
        // (playlists/segments/init), a safe method services/auth/csrf.py never
        // gates. Anonymous viewers have no session cookie at all, so this is a
        // harmless no-op for them — same posture the old bearer injection had.
        // No withCredentials: same-origin XHR sends the cookie anyway, and the
        // flag would survive the 307 to presigned storage, where CORS cannot
        // answer Access-Control-Allow-Credentials.
        enableWorker: true,
        lowLatencyMode: false,
        // Same no-force-live posture as the legacy player (operator-requested)
        // for every consumer except startAtLiveEdge, which needs hls.js's own
        // live-position pick (-1, below) to land near the edge rather than at
        // the oldest buffered segment.
        liveSyncDurationCount: live && startAtLiveEdge ? LIVE_EDGE_SYNC_SEGMENTS : 999,
        liveMaxLatencyDurationCount: 10000,
        startPosition: live && startAtLiveEdge ? -1 : 0,
        // Gap cells are first-class in v2 (EXT-X-GAP); let hls.js walk across them instead of
        // stalling at the first sound-only stretch.
        maxBufferHole: 6,
        backBufferLength: BACK_BUFFER_LENGTH_S,
      });
      hlsRef.current = hls;
      hls.on(Hls.Events.LEVEL_LOADED, (_event, data) => {
        if (destroyed) return;
        // EXT-X-TARGETDURATION, straight off the playlist hls.js just
        // parsed — the real segment length for the live-edge backoff.
        if (typeof data.details?.targetduration === 'number') {
          targetDurationRef.current = data.details.targetduration;
        }
        // First fragment's PROGRAM-DATE-TIME minus its media-timeline start = UTC at
        // media time 0. Known from the playlist alone, so a host seek works before
        // playback has ever produced a `timeupdate`.
        const first = data.details?.fragments?.[0];
        if (first && typeof first.programDateTime === 'number') {
          noteAnchor(first.programDateTime - first.start * 1000);
        }
      });
      hls.on(Hls.Events.ERROR, (_event, data) => {
        if (destroyed || !data.fatal) return;
        if (data.type === Hls.ErrorTypes.NETWORK_ERROR) {
          networkRetriesRef.current += 1;
          if (networkRetriesRef.current > MAX_NETWORK_RETRIES) {
            // Retry budget exhausted (e.g. the master 404s and keeps 404ing) —
            // stop hammering it and tell the parent this session isn't
            // v2-playable right now, instead of retrying forever behind a
            // silent spinner.
            const message = data.details ?? 'network error';
            hls.stopLoad();
            setFatal(message);
            onSeekResolved();
            callbacksRef.current.onFatalError?.(message);
          } else {
            hls.startLoad();
          }
        } else if (data.type === Hls.ErrorTypes.MEDIA_ERROR) {
          const now = Date.now();
          if (now - lastMediaRecoveryAtRef.current > MEDIA_RECOVERY_RESET_MS) {
            mediaRecoveriesRef.current = 0;
          }
          lastMediaRecoveryAtRef.current = now;
          mediaRecoveriesRef.current += 1;
          if (mediaRecoveriesRef.current > MAX_MEDIA_RECOVERIES) {
            const message = data.details ?? 'media error';
            hls.stopLoad();
            setFatal(message);
            onSeekResolved();
            callbacksRef.current.onFatalError?.(message);
          } else {
            hls.recoverMediaError();
          }
        } else {
          const message = data.details ?? 'playback failed';
          setFatal(message);
          onSeekResolved();
          callbacksRef.current.onFatalError?.(message);
        }
      });
      mediaRecoveriesRef.current = 0;
      lastMediaRecoveryAtRef.current = 0;
      hls.loadSource(masterUrl);
      hls.attachMedia(video);
    } else if (video.canPlayType('application/vnd.apple.mpegurl')) {
      video.src = masterUrl; // native Safari — the first device gate proves this path
    } else {
      setFatal('HLS is not supported in this browser');
      onSeekResolved();
    }
    // A revision reload restores play state itself once the new anchor lands
    // (see `noteAnchor` above) — autoPlay only applies to a genuinely fresh mount.
    if (!isRevisionReload) {
      // Chrome-mode's <video> carries no `muted` attribute (see render below;
      // the mute/volume effect owns it after mount) — set it here too so a
      // muted autoplay attempt isn't evaluated against a still-unmuted
      // element before that effect has run.
      video.muted = muted;
      if (autoPlayRef.current) void video.play().catch(() => undefined);
    }

    return () => {
      destroyed = true;
      clearSeekTimeout();
      video.removeEventListener('timeupdate', reportPosition);
      video.removeEventListener('loadedmetadata', noteNativeAnchor);
      video.removeEventListener('timeupdate', syncTransport);
      video.removeEventListener('durationchange', syncTransport);
      video.removeEventListener('loadedmetadata', syncTransport);
      video.removeEventListener('seeked', syncTransport);
      video.removeEventListener('play', onPlay);
      video.removeEventListener('pause', onPause);
      video.removeEventListener('canplay', onCanPlay);
      video.removeEventListener('seeking', onSeeking);
      video.removeEventListener('seeked', onSeekResolved);
      video.removeEventListener('canplay', onSeekResolved);
      hlsRef.current?.destroy();
      hlsRef.current = null;
    };
  }, [masterUrl, coverageLife, mountedSnapshotRev]);

  // Entry-point seek. Anchored (not playhead-relative) on purpose: this runs
  // before playback has produced a position, and the anchor is the only truth
  // available then. Applies once per distinct value — anchorReady also flips
  // during a seamless revision reload, which must not replay a deep link the
  // viewer has since moved past.
  useEffect(() => {
    if (initialSeekUtcMs == null || !anchorReady) return;
    if (appliedSeekRef.current === initialSeekUtcMs) return;
    const video = videoRef.current;
    const anchor = anchorRef.current;
    if (!video || anchor === null) return;
    appliedSeekRef.current = initialSeekUtcMs;
    video.currentTime = clampSeekSeconds(video, (initialSeekUtcMs - anchor) / 1000, {
      live: liveRef.current,
      intent: entrySeekClampIntent(entryIntent),
      playableRanges: playableRangesSeconds(),
    });
  }, [initialSeekUtcMs, anchorReady, playableRangesSeconds, entryIntent]);

  // Volume/mute live on the element only while the shared chrome owns them;
  // with native controls the browser mutates the element itself and mirroring
  // React state onto it would fight the user.
  useEffect(() => {
    const video = videoRef.current;
    if (!video || !chrome) return;
    video.muted = chromeMuted;
    video.volume = volume;
  }, [chrome, chromeMuted, volume]);

  const seekToLive = () => {
    const video = videoRef.current;
    if (!video) return;
    // Same target the automatic live placement applies (see
    // `liveEdgeTargetSeconds`) — the latest published audio/video region,
    // backed off ~2 segments. Matches the resilient player's goLive(): with
    // no published region yet, this is a no-op — never falls back to the
    // raw seekable end, which can land on an unpublished trailing gap.
    const target = liveEdgeTargetSeconds();
    if (!target) return;
    // Only clear a pending automatic placement once this press actually
    // DOES something — a press that lands here with nothing to act on yet
    // must leave it alone, or a "Go live" pressed before any data has
    // loaded would strand the player with neither this seek nor the
    // automatic one ever applied.
    livePlacementPendingRef.current = false;
    video.currentTime = clampSeekSeconds(video, target.seconds, {
      live: true,
      intent: 'explicit',
      playableRanges: [target.rangeSeconds],
    });
  };

  const overlays = (
    <>
      {mediaState === 'audio' && (
        <div className="pointer-events-none absolute left-2 top-2 rounded bg-cyan-500/90 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-black">
          Live sound
        </div>
      )}
      {live && startAtLiveEdge && (
        <button
          type="button"
          onClick={seekToLive}
          className="absolute right-2 top-2 rounded bg-black/70 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-white hover:bg-black/90"
        >
          Go live
        </button>
      )}
      {fatal && (
        <div className="absolute inset-0 flex items-center justify-center bg-black/70 p-4 text-center text-xs text-white">
          {fatal}
        </div>
      )}
      {!fatal && seekPending && (
        <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
          <Spinner size={24} />
        </div>
      )}
      {!fatal && seekTimedOut && (
        <div className="pointer-events-none absolute inset-0 flex items-center justify-center bg-black/70 p-4 text-center text-xs text-white">
          still working on that — try seeking again
        </div>
      )}
    </>
  );

  if (!chrome) {
    return (
      <div className={cn('relative h-full w-full bg-black', className)}>
        <video
          ref={videoRef}
          className="h-full w-full object-contain"
          controls
          muted={muted}
          playsInline
        />
        {overlays}
      </div>
    );
  }

  const transport: PlayerTransport = {
    isPlaying,
    currentTime: position,
    duration,
    ready: canPlay,
    muted: chromeMuted,
    volume,
    togglePlay: () => {
      const video = videoRef.current;
      if (!video) return;
      if (video.paused) void video.play().catch(() => undefined);
      else video.pause();
    },
    seek: (seconds) => {
      const video = videoRef.current;
      if (!video) return;
      livePlacementPendingRef.current = false;
      video.currentTime = clampSeekSeconds(video, seconds, {
        live: liveRef.current,
        intent: 'explicit',
        playableRanges: playableRangesSeconds(),
      });
      setPosition(video.currentTime);
    },
    setMuted: setChromeMuted,
    setVolume: (next) => {
      setVolume(next);
      if (next > 0 && chromeMuted) setChromeMuted(false);
    },
  };

  if (renderControls) {
    return (
      <div
        className={cn('relative h-full w-full bg-black', className)}
        onClickCapture={tapForSound.onGesture}
        onKeyDownCapture={tapForSound.onGesture}
        onPointerDownCapture={tapForSound.onGesture}
      >
        <video ref={videoRef} className="h-full w-full object-contain" style={rotationStyle} playsInline />
        {overlays}
        {renderControls(transport, {
          tapForSound: tapForSound.visible && !fatal && audioExpected !== false,
        })}
      </div>
    );
  }

  return (
    <div
      className="contents"
      onClickCapture={tapForSound.onGesture}
      onKeyDownCapture={tapForSound.onGesture}
      onPointerDownCapture={tapForSound.onGesture}
    >
      <PlayerChrome
        transport={transport}
        variant={chrome}
        heightFit={heightFit}
        glassRail={glassRail}
        onMaximize={onMaximize}
        isMaximized={maximized}
        audioExpected={audioExpected}
        onControlsHiddenChange={onControlsHiddenChange}
      >
        {({ fitTiles }) => (
          <div className={cn('relative flex-grow bg-black', fitTiles && 'min-h-0', className)}>
            <video ref={videoRef} className="h-full w-full object-contain" style={rotationStyle} playsInline />
            {overlays}
            {tapForSound.visible && !fatal && audioExpected !== false && <TapForSoundOverlay />}
          </div>
        )}
      </PlayerChrome>
    </div>
  );
});

/**
 * Rollout flag: `VITE_RESILIENT_PLAYBACK=true` at build time, overridable per
 * page load via `?resilient=1` / `?resilient=0` (either direction) so
 * staging can flip one session without a rebuild. Dispatches to a whole
 * separate component per mode — never both — so the disabled path never
 * pays for the resilient controller's Hls instances, fetch, or timers, and
 * flag-off behaviour (and its tests) stay byte-for-byte the legacy player.
 */
const V2Player = forwardRef<V2PlayerHandle, V2PlayerProps>(function V2Player(props, ref) {
  const resilient = useMemo(
    () =>
      resolveResilientFlag(import.meta.env.VITE_RESILIENT_PLAYBACK === 'true', window.location.search) &&
      // Resilient mode's mode/drift decisions depend on hls.js's own
      // per-fragment gap/PROGRAM-DATE-TIME data, which native HLS (Safari)
      // never exposes — fall back to the legacy single-element player there
      // rather than mount a controller that can never establish an anchor.
      Hls.isSupported(),
    [],
  );
  return resilient ? <ResilientV2Player {...props} ref={ref} /> : <LegacyV2Player {...props} ref={ref} />;
});

export default V2Player;
